import { CommonFilterModel, CommonSortModelResource } from '@/api/resources/common/shared/models/common.models'
import { AgGridCallbackEvent, AgGridCallbackEventType } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { InfinitePaginatorResourceService, InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { Nullable } from '@/shared/types/app.types'
import { IServerSideDatasource, IServerSideGetRowsParams } from '@ag-grid-enterprise/all-modules'
import { GridUtils } from '@/shared/util/grid-utils'

export class InfiniteDatasource<T> implements IServerSideDatasource {
    service: InfinitePaginatorResourceService<T> | null
    nextKey: Nullable<string>
    lastRow: number
    lastContent: T[]

    constructor(service: InfinitePaginatorResourceService<T> | null) {
        this.service = service
        this.nextKey = null
        this.lastRow = 0
        this.lastContent = []
    }

    async getRows(params: IServerSideGetRowsParams): Promise<void> {
        try {
            const requestStartRow = params.request.startRow ?? 0
            const requestEndRow = params.request.endRow ?? 0
            const size = requestEndRow - requestStartRow
            if (requestStartRow === 0) {
                this.nextKey = null
                this.lastRow = 0
                this.lastContent = []
            }

            const paginator = await this.getPaginator(params.request.filterModel, params.request.sortModel, size)
            const sizePaginator = paginator.content.length
            if (sizePaginator === 0 && params.request.startRow !== 0) {
                params.api.dispatchEvent({
                    type: AgGridCallbackEventType.WARN,
                    error: i18n.tc('statisticalAnalysis.modals.gridNoMorePage')
                } as AgGridCallbackEvent)

                params.success({ rowData: this.lastContent, rowCount: this.lastRow })
            } else {
                this.nextKey = paginator.scrollPage.scrollId
                this.lastRow += sizePaginator
                this.lastContent = paginator.content
                if (sizePaginator < size) {
                    params.success({ rowData: paginator.content, rowCount: this.lastRow })
                } else {
                    params.success({ rowData: paginator.content, rowCount: -1 })
                }
            }

            params.api.dispatchEvent({
                type: AgGridCallbackEventType.SUCCESS
            } as AgGridCallbackEvent)
        } catch (error) {
            console.error(error)
            params.fail()

            params.api.dispatchEvent({
                type: AgGridCallbackEventType.FAIL,
                error
            } as AgGridCallbackEvent)
        } finally {
            params.api.dispatchEvent({
                type: AgGridCallbackEventType.END
            } as AgGridCallbackEvent)
        }
    }

    getPaginator(
        filterModel: CommonFilterModel,
        sortModel: CommonSortModelResource[],
        size: number
    ): Promise<InfinitePaginatorResponse<T>> {
        if (!this.service) {
            throw new Error('undefined service')
        }

        const params = {
            sort: GridUtils.getSortValue(sortModel),
            size
        }
        return this.service.paginator(filterModel, params, this.nextKey)
    }
}
